import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { TransactionEMSPInterFace } from '../../../interface/emsp-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disablePaymentManagementApplied,
  fetchAllPayments,
  fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
  refreshAllPayments,
} from '../../../redux/slices/EMSPCommercialTransactionSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import EmspCdrSidePanel from '../../../shared-components/table-side-panel/EmspCdrSidePanel';
import EmspSessionSidePanel from '../../../shared-components/table-side-panel/EmspSessionSidePanel';
import EndConsumerSidePanel from '../../../shared-components/table-side-panel/EndConsumerSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatDuration from '../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import paymentsApi from '../../../utils/api/v2/emsp-transaction';

/**
 * Payments Management function Component
 * @returns  The rendered component.
 */
function CommercialTransactionList(): JSX.Element {
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();

  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.emspCommercialTransaction);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Fetch the payment details in redus
   * @param {any} data - The data
   */
  const fetchPayments = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(fetchAllPayments(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...queryParameters, page, limit, ...sortValue });
  };

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: payments,
    disableEdit: true, // ← hides the Edit (pencil) button
    disableDelete: true, // ← hides the Delete (trash) button
  });
  const column = [
    {
      label: t('CDRId'),
      accessorKey: 'externalId' as keyof TransactionEMSPInterFace,
      queryParamsKey: 'cdrObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <EmspCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('driverId'),
      accessorKey: 'userName' as keyof TransactionEMSPInterFace,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sortAllow: true,
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      accessorKey: 'sessionId' as any,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
    },
    {
      label: t('partyId', { defaultValue: 'Party ID' }),
      accessorKey: 'partyId' as any,
      sortAllow: true,
      /**
       * Render Party ID.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Party ID element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{rowData.partyId || (rowData as any).cdrDetails?.partyId || t('na')}</span>
      ),
    },

    // {
    //   label: t('location', { defaultValue: 'Location' }),
    //   accessorKey: 'location' as any,
    //   /**
    //    * Custom rendering of Location column.
    //    * @param {TransactionEMSPInterFace} rowData - The row data.
    //    * @returns  The rendered location name.
    //    */
    //   customData: (rowData: TransactionEMSPInterFace) => {
    //     const locationName = rowData.location?.name || rowData.chargingPointSn || t('na');
    //     return <span>{locationName}</span>;
    //   },
    // },
    {
      label: t('energy'),
      accessorKey: 'totalEnergy' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total energy in kWh.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns  The total energy element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{ToFixedValue(rowData.totalEnergy, 3)} kWh</span>
      ),
    },
    {
      label: t('CDRamount'),
      accessorKey: 'totalCost' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.totalCost !== undefined && rowData.totalCost !== null
            ? `${Number(rowData.totalCost).toFixed(3)} ${rowData.currency || 'SAR'}`
            : `0.000 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('platformAmount'),
      accessorKey: 'platform' as keyof TransactionEMSPInterFace,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.platformAmount
            ? `${ToFixedValue(Number(rowData.platformAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('sessionAmount'),
      accessorKey: 'session' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render session amount with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The session amount element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.sessionAmount 
            ? `${ToFixedValue(Number(rowData.sessionAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('finalAmount'),
      accessorKey: 'finalAmount' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns  The total cost element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>
          {rowData.finalAmount !== undefined && rowData.finalAmount !== null
            ? `${ToFixedValue(rowData.finalAmount)} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },

    {
      label: t('locationId'),
      accessorKey: 'locationId' as keyof TransactionEMSPInterFace,
      /**
       * Custom rendering of Location ID column.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns  The rendered location ID.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        const locationId = rowData.location?.id || rowData.locationId || t('na');
        return <span>{locationId}</span>;
      },
    },
    {
      label: t('duration', { defaultValue: 'Time' }),
      accessorKey: 'totalTime' as keyof TransactionEMSPInterFace,
      sortAllow: true,
      /**
       * Render duration using FormatDuration helper.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        let totalSeconds = 0;

        if (rowData.totalTime != null) {
          totalSeconds = Math.round(Number(rowData.totalTime) * 3600);
        } else if (rowData.startTime && rowData.endTime) {
          const diffMs =
            new Date(rowData.endTime).getTime() - new Date(rowData.startTime).getTime();

          if (diffMs > 0) {
            totalSeconds = Math.floor(diffMs / 1000);
          }
        }

        return <span>{FormatDuration(totalSeconds, t('na'))}</span>;
      },
    },
    {
      label: t('invoiceId', { defaultValue: 'Invoice ID' }),
      accessorKey: 'invoiceNumber' as any,
      sortAllow: true,
      /**
       * Render Invoice ID.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Invoice ID element.
       */
      customData: (rowData: TransactionEMSPInterFace) => (
        <span>{rowData.invoiceNumber || (rowData as any).invoiceId || t('na')}</span>
      ),
    },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'paymentStatus' as keyof TransactionEMSPInterFace,
      sortAllow: false,
      /**
       * Render Payment Status badge.
       * @param {TransactionEMSPInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Payment Status badge element.
       */
      customData: (rowData: TransactionEMSPInterFace) => {
        const rawStatus =
          rowData.paymentStatus ||
          rowData.billingStatus ||
          (rowData.isBilled || rowData.isPaid ? 'SUCCESS' : 'PENDING');

        const upperStatus = String(rawStatus || '').toUpperCase();

        let statusClass = 'pending';
        let statusLabel = upperStatus;

        if (upperStatus === 'SUCCESS' || upperStatus === 'PAID') {
          statusClass = 'success';
          statusLabel = t('paid', { defaultValue: 'Paid' });
        } else if (upperStatus === 'FAILED' || upperStatus === 'UNPAID') {
          statusClass = 'failed';
          statusLabel = t('failed', { defaultValue: 'Failed' });
        } else {
          statusClass = 'pending';
          statusLabel = t('pending', { defaultValue: 'Pending' });
        }

        return (
          <span className={`billing-status-badge ${statusClass}`}>
            <span className="dot" />
            {statusLabel}
          </span>
        );
      },
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('transactionId'), accessorKey: 'externalId' },
    { label: t('driverId'), accessorKey: 'userName' },
    { label: t('sessionId', { defaultValue: 'Session ID' }), accessorKey: 'sessionId' },
    { label: t('partyId', { defaultValue: 'Party ID' }), accessorKey: 'partyId' },
    { label: t('energy'), accessorKey: 'totalEnergy' },
    { label: t('CDRamount'), accessorKey: 'totalCost' },
    { label: t('mobilityAmount'), accessorKey: 'platform' },
    { label: t('sessionAmount'), accessorKey: 'sessionAmount' },
    { label: t('finalAmount'), accessorKey: 'finalAmount' },
    { label: t('locationId'), accessorKey: 'locationId' },
    { label: t('duration', { defaultValue: 'Time' }), accessorKey: 'totalTime' },
    { label: t('invoiceId', { defaultValue: 'Invoice ID' }), accessorKey: 'invoiceNumber' },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'billingStatus',
    },
  ];

  /**
   * Table pagination function
   * @param {any} value - The pagination limit value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    const apiCall = paymentsApi.fetchCommercialTransactionExport(payload);
    apiCall
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TransactionEMSPInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'externalId':
                return row.transactionId || row.externalId || t('na');

              case 'userName':
                return row.userCustomId || row.userName || t('na');

              case 'locationId':
                return row.location?.id || row.locationId || t('na');

              case 'totalEnergy':
                return row.totalEnergy !== undefined && row.totalEnergy !== null
                  ? `${ToFixedValue(row.totalEnergy, 3)} kWh`
                  : t('na');

              case 'totalCost':
                return row.totalCost !== undefined && row.totalCost !== null
                  ? `${ToFixedValue(row.totalCost)} ${row.currency || 'SAR'}`
                  : t('na');

              case 'totalTime': {
                let displayTime = t('na');
                if (row.totalTime !== undefined && row.totalTime !== null) {
                  displayTime = `${Math.round(row.totalTime * 60)} min`;
                } else if (row.startTime && row.endTime) {
                  const diffMs =
                    new Date(row.endTime).getTime() - new Date(row.startTime).getTime();
                  if (diffMs > 0) {
                    displayTime = `${Math.round(diffMs / 60000)} min`;
                  }
                }
                return displayTime;
              }

              case 'platform':
                return row.platformAmount !== undefined && row.platformAmount !== null
                  ? `${ToFixedValue(row.platformAmount)} ${row.currency || 'SAR'}`
                  : `0.00 ${row.currency || 'SAR'}`;

              case 'finalAmount':
                return row.finalAmount !== undefined && row.finalAmount !== null
                  ? `${ToFixedValue(row.finalAmount)} ${row.currency || 'SAR'}`
                  : `0.00 ${row.currency || 'SAR'}`;

              case 'sessionId':
                return row.sessionId || (row as any).cdrDetails?.sessionId || t('na');

              case 'partyId':
                return row.partyId || (row as any).cdrDetails?.partyId || t('na');

              case 'billingStatus':
                return (
                  (row as any).paymentStatus ||
                  row.billingStatus ||
                  (row as any).payment?.billingStatus ||
                  t('na')
                );

              case 'invoiceNumber':
                return row.invoiceNumber || (row as any).invoiceId || t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'payments-list.csv');
          } else {
            exportPDF(headers, rows, 'payments-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to auto refresh data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAllPayments({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    if (isFilterUpdated || payments.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPayments({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('transactions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  return (
    <div className="payment-management-wrap">
      <div className="table-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          data={payments}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPaymentMethodFilters}
          hidedColumnNames={[
            'sessionAmount',
            'platformAmount',
            'networkRevenue',
            'sharableRevenue',
          ]}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          exportDataPdfOrCSV={
            permissionCheck('mobilityTransactionsExport') ? exportDataPdfOrCSV : undefined
          }
          created
          lastUpdated
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
        />
      </div>
    </div>
  );
}

export default CommercialTransactionList;
